import * as Sentry from '@sentry/react';

const KEY = 'post-login-redirect';
// Ignore a target older than this so an abandoned link can't hijack a later login.
const MAX_AGE_MS = 10 * 60 * 1000;

interface StashedRedirect {
  path: string;
  ts: number;
}

// Open-redirect guard: same-origin in-app paths only. Rejects absolute URLs,
// protocol-relative (`//evil.com`), and anything not rooted at `/`.
export function isSafeInternalPath(value: unknown): value is string {
  return typeof value === 'string' && /^\/(?!\/)/.test(value);
}

// Query param appended to the login URL (/login?redirect=<path>) as a visible,
// storage-independent fallback. The stash is still primary — it's the only channel
// that survives the SSO round-trip through the IdP.
export const REDIRECT_PARAM = 'redirect';

export function stashPostLoginRedirect(path: string): void {
  if (!isSafeInternalPath(path)) return;
  const entry: StashedRedirect = { path, ts: Date.now() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    // sessionStorage unavailable — ?redirect= on the login URL still carries it.
  }
}

// Builds /login?redirect=<encoded path> for the unauthenticated deep-link path.
export function loginUrlWithRedirect(loginPath: string, target: string): string {
  if (!isSafeInternalPath(target) || target === '/') return loginPath;
  return `${loginPath}?${REDIRECT_PARAM}=${encodeURIComponent(target)}`;
}

function readStash(): string | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StashedRedirect>;
    if (!isSafeInternalPath(parsed.path)) return null;
    if (typeof parsed.ts !== 'number' || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed.path;
  } catch {
    Sentry.addBreadcrumb({ category: 'deep-link', level: 'warning', message: 'Malformed post-login redirect stash' });
    return null;
  }
}

export function clearPostLoginRedirect(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear
  }
}

// Resolves and clears the post-login target. Prefers the stash (survives SSO),
// then ?redirect= on the current URL, then "/".
export function consumePostLoginRedirect(): string {
  const stashed = readStash();
  if (stashed) return stashed;

  try {
    const fromQuery = new URLSearchParams(window.location.search).get(REDIRECT_PARAM);
    if (isSafeInternalPath(fromQuery)) return fromQuery;
  } catch {
    // no window / malformed search — fall through
  }
  return '/';
}
